import { RouteProps } from 'routes/AppRouter'
import genericStyle from '../../common/utils/generic.module.css'
import { useTranslation } from 'react-i18next'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { navigate } from '@reach/router'
import { ROUTE_PATIENTS } from '../../routes/routes-constants'
import styles from './Editor.module.css'
import { RolesService } from '../../modules/users/services/RolesServices'
import { getUserContainer } from '../../container/user-module'
import {
  IUserService,
  LOGGED_USER_SERVICE_KEY,
  ROLES_SERVICE_KEY,
  USER_SERVICE_KEY,
} from '../../modules/users'
import { Role } from 'modules/users/models/Role'
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Typography,
  TextField,
  Box,
  RadioGroup,
  Radio,
  Chip,
} from '@mui/material'
import { emptyUserDTO, fromModel, User, UserDTO } from '../../modules/users/models/User'
import { Alert } from '@material-ui/lab'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'
import { IStatusService } from '../../common/status/StatusService'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { IPatologyService, PATOLOGY_SERVICE_KEY } from '../../modules/patologies'
import { Query } from '../../common/api/Query'
import { getPatologyContainer } from '../../container/patologies-module'
import { useIsOpenMenuLeftContext } from '../../common/utils/isOpenMenuLeftContext'
import { Input } from '../../pages/user-profile/userProfileStylesMui'
import { InputTextDescription } from '../resource/userProfileStylesMui'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import calendarIcon from '../../assets/calendar/calendar-newEvent.svg'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { Subscription } from 'rxjs'
import {
  validateName,
  validateEmail,
  validateHistoryNumber,
  validateDiagnosisSummary,
  validateRequiredText,
  validateDate,
  sanitizeNumericInput,
  MAX_NAME_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_HISTORY_NUMBER,
  MAX_DIAGNOSIS_SUMMARY_LENGTH,
} from '../../common/utils/validationUtils'

const roleContainer = getUserContainer()
const roleService = roleContainer.get<RolesService>(ROLES_SERVICE_KEY)
const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const patologyService = getPatologyContainer().get<IPatologyService>(PATOLOGY_SERVICE_KEY)
const loggedUserService = getUserContainer().get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

interface EditorProps extends RouteProps {
  id?: string
}

export function Editor(props: EditorProps) {
  const { innerWidth } = window
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [patient, setPatient] = useState<UserDTO>(emptyUserDTO())
  const [userLogged, setUserLogged] = useState<User>()
  const [selectedPatologies, setSelectedPatologies] = useState<string[]>([])
  const [possiblePatologies, setPossiblePatologies] = useState<string[]>([])
  const [translatePatologies, setTranslatePatologies] = useState(new Map<string, string>())
  const [dateTimePickerError, setDateTimePickerError] = useState<string>('')
  const [formError, setFormError] = useState<string>('')
  const { openMenuLeft } = useIsOpenMenuLeftContext()

  useEffect(() => {
    if (!loggedUser?.id) return
    const subscription = userService.getByID(loggedUser?.id).subscribe((lu) => {
      setUserLogged(lu)
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    const sub1 = patologyService.getFilteredList(new Query({})).subscribe((res) => {
      const pts: string[] = []
      const trans = new Map<string, string>()
      if (res) {
        for (let i = 0; i < res.count; i++) {
          pts.push(t(res.items[i].name))
          trans.set(t(res.items[i].name), res.items[i].name)
        }
      }
      setTranslatePatologies(trans)
      setPossiblePatologies(pts)
    })

    let sub2: Subscription | null = null
    if (props.id) {
      sub2 = userService.getByID(props.id).subscribe((res) => {
        if (res) {
          const dto = fromModel(res)
          setPatient(dto)
        }
      })
    }

    return () => {
      sub1.unsubscribe()
      sub2?.unsubscribe()
    }
  }, [props.id])

  useEffect(() => {
    if (patient.patologies && patient.patologies.length > 0 && translatePatologies.size > 0) {
      const translatedPats = patient.patologies
        .map((p) => {
          const entry = Array.from(translatePatologies.entries()).find(([_, orig]) => orig === p)
          return entry ? entry[0] : p
        })
        .filter((p) => p !== '')
      setSelectedPatologies(translatedPats)
    }
  }, [patient.patologies, translatePatologies])

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPatient({ ...patient, [e.target.id]: e.target.value })
    setFormError('')
  }

  const handleAutocompleteChange = (pat: string[]) => {
    const trans: string[] = []
    pat.forEach((element: string) => {
      const original = translatePatologies.get(element)
      if (original) trans.push(original)
    })
    setPatient({ ...patient, patologies: trans })
  }

  const handleGender = (e: ChangeEvent<HTMLInputElement>) => {
    const newGender = e.target.value === 'male' ? 1 : 2
    setPatient({ ...patient, gender: newGender })
    setFormError('')
  }

  const handleBack = () => navigate(ROUTE_PATIENTS)

  const validateForm = (): boolean => {
    const errors: string[] = []

    const firstNameValidation = validateName(patient.firstName)
    if (!firstNameValidation.isValid) {
      if (firstNameValidation.error === 'required') {
        errors.push(t('name'))
      } else {
        errors.push(t('name') + ' (' + t('invalidFormat') + ')')
      }
    }

    const lastNameValidation = validateName(patient.lastName)
    if (!lastNameValidation.isValid) {
      if (lastNameValidation.error === 'required') {
        errors.push(t('lastName'))
      } else {
        errors.push(t('lastName') + ' (' + t('invalidFormat') + ')')
      }
    }

    const emailValidation = validateEmail(patient.email)
    if (!emailValidation.isValid) {
      if (emailValidation.error === 'required') {
        errors.push(t('email'))
      } else {
        errors.push(t('email') + ' (' + t('invalidFormat') + ')')
      }
    }

    const historyNumberValidation = validateHistoryNumber(patient.historyRecordNumber)
    if (!historyNumberValidation.isValid) {
      if (historyNumberValidation.error === 'required') {
        errors.push(t('historyNumber'))
      } else {
        errors.push(t('historyNumber') + ' (' + t('invalidFormat') + ')')
      }
    }

    const diagnosisSummaryValidation = validateDiagnosisSummary(patient.diagnosisSummary)
    if (!diagnosisSummaryValidation.isValid) {
      if (diagnosisSummaryValidation.error === 'required') {
        errors.push(t('diagnosisSummary'))
      } else {
        errors.push(t('diagnosisSummary') + ' (' + t('tooLong') + ')')
      }
    }

    const diagnosisValidation = validateRequiredText(patient.diagnosis)
    if (!diagnosisValidation.isValid) {
      errors.push(t('diagnosis'))
    }

    const dateValidation = validateDate(patient.dob)
    if (!dateValidation.isValid) {
      if (dateValidation.error === 'required') {
        errors.push(t('birthDate'))
      } else {
        errors.push(t('birthDate') + ' (' + t('invalidFormat') + ')')
      }
    }

    if (!patient.gender) {
      errors.push(t('gender'))
    }

    if (!patient.patologies || patient.patologies.length === 0) {
      errors.push(t('patologies'))
    }

    if (errors.length > 0) {
      setFormError(t('requiredFields') + ': ' + errors.join(', '))
      return false
    }

    setFormError('')
    return true
  }

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setPatient({ ...patient, dob: date })
      setFormError('')
    }
  }

  const handleHistoryNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeNumericInput(e.target.value)
    setPatient({ ...patient, historyRecordNumber: Number(sanitized) || 0 })
    setFormError('')
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const newPatient: UserDTO = {
      ...patient,
      language: userLogged?.language ?? patient.language,
      assignedID: loggedUserService.get()?.id || '',
      isPatient: true,
      isActive: true,
    }

    if (props.id) {
      const subscription = userService.update(newPatient).subscribe(() => {
        statusService.sendStatus({ variant: 'success' })
        handleBack()
      })
      return () => subscription.unsubscribe()
    } else {
      const subscription = userService.add(newPatient, navigator.language).subscribe((res) => {
        if (res) {
          statusService.sendStatus({ variant: 'success' })
          handleBack()
        }
      })
      return () => subscription.unsubscribe()
    }
  }

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginLeft: openMenuLeft ? '50px' : '',
      }}
    >
      <div
        style={{ maxWidth: openMenuLeft ? 'calc(100% - 250px)' : 'calc(100% - 300px)' }}
        className={genericStyle.pageContainer}
      >
        <Box className={genericStyle.container} sx={{ maxHeight: 'initial' }}>
          <form className={styles.form} id={'editUser'} onSubmit={handleSave}>
            <Box>
              <Box display={'flex'} justifyContent={'space-between'}>
                <Box className={styles.box}>
                  <Typography className={styles.inputTitle}>{t('name')}</Typography>
                  <Input
                    sx={{ width: '100%' }}
                    value={patient.firstName}
                    fullWidth
                    id="firstName"
                    label={''}
                    variant="outlined"
                    onChange={(e) => handleChange(e)}
                    inputProps={{ maxLength: MAX_NAME_LENGTH }}
                  />
                </Box>
                <Box className={styles.box}>
                  <Typography className={styles.inputTitle}>{t('lastName')}</Typography>
                  <Input
                    sx={{ width: '100%' }}
                    value={patient.lastName}
                    id="lastName"
                    label={''}
                    variant="outlined"
                    onChange={(e) => handleChange(e)}
                    inputProps={{ maxLength: MAX_NAME_LENGTH }}
                  />
                </Box>
              </Box>
              <Box display={'flex'} justifyContent={'space-between'}>
                <Box className={styles.box}>
                  <Typography className={styles.inputTitle}>{t('email')}</Typography>
                  <Input
                    sx={{ width: '100%' }}
                    value={patient.email}
                    id="email"
                    label={''}
                    variant="outlined"
                    type="email"
                    onChange={(e) => handleChange(e)}
                    inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
                  />
                </Box>
                <Box className={styles.box}>
                  <Typography className={styles.inputTitle}>{t('patologies')}</Typography>
                  <Autocomplete
                    multiple
                    id="patologies"
                    options={possiblePatologies}
                    value={selectedPatologies}
                    onChange={(_, v) => {
                      setSelectedPatologies(v)
                      handleAutocompleteChange(v)
                      setFormError('')
                    }}
                    ChipProps={{
                      onDelete: undefined,
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '32px',
                            padding: '3px 8px 0px 8px !important',
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'flex-start',
                          },
                          '& .MuiChip-deleteIcon': {
                            color: 'rgba(0, 0, 0, 0.4) !important',
                          },
                          // Estilos para los chips
                          '& .MuiChip-root': {
                            margin: '2px',
                            maxWidth: '100%',
                            background: '#e0e0e0',
                            borderRadius: '35px',
                            height: '25px !important',
                            fontWeight: 600,
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          onDelete={() => {
                            const newSelected = selectedPatologies.filter((_, i) => i !== index)
                            setSelectedPatologies(newSelected)
                            handleAutocompleteChange(newSelected)
                            setFormError('')
                          }}
                          sx={{
                            backgroundColor: '#e0e0e0',
                            '& .MuiChip-deleteIcon': {
                              color: '#e0e0e0',
                            },
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        />
                      ))
                    }
                    noOptionsText={t('noElementsAvailable')}
                    ListboxProps={{
                      style: {
                        maxHeight: '250px',
                      },
                    }}
                  />
                </Box>
              </Box>
              <Box display={'flex'} justifyContent={'space-between'}>
                <Box className={styles.box}>
                  <FormControl>
                    <FormLabel className={styles.inputTitle} id="gender">
                      {t('gender')}
                    </FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="gender"
                      name="gender"
                      value={patient.gender === 1 ? 'male' : 'female'}
                      onChange={(e) => handleGender(e)}
                    >
                      <FormControlLabel
                        value="male"
                        control={<Radio />}
                        sx={{ marginLeft: '0px' }}
                        label={t('male')}
                      />
                      <FormControlLabel
                        value="female"
                        control={<Radio />}
                        sx={{ marginLeft: '0px' }}
                        label={t('female')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                <Box className={styles.box}>
                  <Typography className={styles.inputTitle}>{t('historyNumber')}</Typography>
                  <Input
                    sx={{ width: '100%' }}
                    id="historyRecordNumber"
                    label={''}
                    value={patient.historyRecordNumber || ''}
                    variant="outlined"
                    onChange={handleHistoryNumberChange}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                  />
                </Box>
              </Box>
              <Box display={'flex'} justifyContent={'space-between'}>
                <Box className={styles.box}>
                  <Typography className={styles.inputTitle}>{t('diagnosisSummary')}</Typography>
                  <Input
                    sx={{ width: '100%' }}
                    value={patient.diagnosisSummary}
                    id="diagnosisSummary"
                    name="diagnosisSummary"
                    label={''}
                    variant="outlined"
                    onChange={(e) => handleChange(e)}
                    inputProps={{ maxLength: MAX_DIAGNOSIS_SUMMARY_LENGTH }}
                  />
                </Box>
                <Box className={styles.box}>
                  <Typography className={styles.inputTitle}>{t('birthDate')}</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                    <DatePicker
                      value={patient.dob}
                      inputFormat={'DD/MM/YYYY'}
                      maxDate={new Date()}
                      onChange={handleDateChange}
                      label={''}
                      onError={(reason) => {
                        switch (reason) {
                          case 'invalidDate':
                            setDateTimePickerError(t('invalidDateMessage'))
                            break
                          case 'minDate':
                            setDateTimePickerError(t('minDateMessage'))
                            break
                          case 'maxDate':
                            setDateTimePickerError(t('maxDateMessage'))
                            break
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          id="dob"
                          sx={{
                            width: '100%',
                            '& fieldset': {
                              borderRadius: 32,
                              border: '1px solid #E8E7EC',
                              fontFamily: 'Montserrat',
                            },
                            '& .MuiInputBase-root': { fontFamily: 'Montserrat' },
                            '& .MuiOutlinedInput-root': {
                              border: 'none',
                              '&.Mui-focused fieldset': {
                                borderColor: '#ADB84E',
                              },
                            },
                          }}
                          size={'small'}
                          helperText={params.error && dateTimePickerError}
                          {...params}
                        />
                      )}
                      components={{
                        OpenPickerIcon: () => (
                          <img src={calendarIcon} alt="calendar" style={{ marginRight: 8 }} />
                        ),
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
              <Box display={'flex'} justifyContent={'space-between'}>
                <Box className={styles.box}>
                  <Typography className={styles.inputTitle}>{t('diagnosis')}</Typography>
                  <InputTextDescription
                    sx={{ width: '100%' }}
                    multiline
                    rows={6}
                    value={patient.diagnosis}
                    id="diagnosis"
                    name="diagnosis"
                    label={''}
                    size={'medium'}
                    variant="outlined"
                    onChange={(e) => handleChange(e)}
                  />
                </Box>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  className={styles.box}
                >
                  <Box className={styles.buttons} mb={3} display="flex" justifyContent={'center'}>
                    <Box style={{ marginRight: 8 }}>
                      <Button className={styles.buttonCancel} type={'button'} onClick={handleBack}>
                        {t('back')}
                      </Button>
                    </Box>
                    <Button className={styles.buttonAdd} type={'submit'}>
                      {props.id ? t('editIcon') : t('add')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </form>
          {formError && (
            <Box mb={3} mt={2}>
              <Alert severity="error">{formError}</Alert>
            </Box>
          )}
        </Box>
      </div>
    </div>
  )
}
